import { FunctionComponent } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { styles } from '../../../../styles/themes/style';
import { ISignalPerson, SignalLeadStatus, SignalType } from '../../../../interfaces/models/signals';

/** Shared look of the Signals page: cards, pills, avatars, the small icon set. */

const ICON_PATHS = {
  eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
  briefcase: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 13h18" /></>,
  comment: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></>,
  heart: <><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21l7.8-7.5 1-1.1a5.5 5.5 0 0 0 0-7.8z" /></>,
  post: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></>,
  plus: <><path d="M12 5v14M5 12h14" /></>,
  trash: <><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" /></>,
  external: <><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  check: <><path d="M20 6 9 17l-5-5" /></>,
  x: <><path d="M18 6 6 18M6 6l12 12" /></>,
  send: <><path d="M3 11l19-9-9 19-2-8-8-2z" /></>,
  undo: <><path d="M9 14 4 9l5-5" /><path d="M4 9h11a5 5 0 0 1 0 10h-1" /></>,
  alert: <><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></>,
  radar: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /><path d="m12 12 6-6" /></>,
  chevron: <><path d="m9 6 6 6-6 6" /></>,
};

export type SignalIconName = keyof typeof ICON_PATHS;

export const Icon: FunctionComponent<{ name: SignalIconName; size?: number }> = ({ name, size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {ICON_PATHS[name]}
  </svg>
);

export const SIGNAL_TYPE_ICONS: Record<SignalType, SignalIconName> = {
  profile_view: 'eye',
  post_comment: 'comment',
  post_reaction: 'heart',
  job_change: 'briefcase',
};

export const STATUS_TONES: Record<SignalLeadStatus, { bg: string; fg: string }> = {
  new: { bg: styles.colors.primary100, fg: styles.colors.primary600 },
  approved: { bg: styles.colors.green200, fg: '#0f9d6c' },
  dismissed: { bg: styles.colors.black100, fg: styles.colors.black400 },
  in_campaign: { bg: styles.colors.yellow200, fg: '#c48a00' },
};

export function personName(person: ISignalPerson): string {
  return person.name || [person.firstName, person.lastName].filter(Boolean).join(' ') || person.publicIdentifier || 'LinkedIn member';
}

export function personUrl(person: ISignalPerson): string | undefined {
  if (person.profileUrl) return person.profileUrl;
  if (person.publicIdentifier) return `https://www.linkedin.com/in/${encodeURIComponent(person.publicIdentifier)}`;
  if (person.providerId) return `https://www.linkedin.com/in/${encodeURIComponent(person.providerId)}`;
  return undefined;
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('') || '?';
}

export function fromNow(date?: string | null): string {
  if (!date) return '—';
  const m = moment(date);
  return m.isValid() ? m.fromNow() : '—';
}

export function plural(n: number, word: string, many = `${word}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? word : many}`;
}

export const Avatar: FunctionComponent<{ person: ISignalPerson; size?: number }> = ({ person, size = 36 }) => {
  const name = personName(person);
  return person.profilePicture
    ? <AvatarImg src={person.profilePicture} alt="" $size={size} referrerPolicy="no-referrer" />
    : <AvatarInitials $size={size}>{initials(name)}</AvatarInitials>;
};

// ─── Styles ───────────────────────────────────────────────────────────────────

export const Card = styled.section`
  background: ${styles.colors.white};
  border: 1px solid #e1e5eb;
  border-radius: 18px;
  padding: 20px 24px;
  display: flex; flex-direction: column; gap: 16px;
`;

export const CardHead = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap;
`;

export const CardTitle = styled.h2`
  margin: 0;
  font-family: NunitoSans; font-size: 16px; font-weight: 800; line-height: 22px;
  color: ${styles.colors.black600};
`;

export const Muted = styled.span`
  font-family: NunitoSans; font-size: 13px; line-height: 18px;
  color: ${styles.colors.black400};
`;

export const Pill = styled.span<{ $bg: string; $fg: string }>`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 9px; border-radius: 9999px;
  font-family: NunitoSans; font-size: 12px; font-weight: 700; line-height: 18px;
  background: ${({ $bg }) => $bg}; color: ${({ $fg }) => $fg};
  white-space: nowrap;
`;

export const GhostButton = styled.button<{ $danger?: boolean }>`
  display: inline-flex; align-items: center; gap: 6px;
  height: 32px; padding: 0 12px; border-radius: 8px;
  border: 1px solid #dde3ee; background: ${styles.colors.white}; cursor: pointer;
  font-family: NunitoSans; font-size: 13px; font-weight: 700;
  color: ${({ $danger }) => ($danger ? styles.colors.red600 : styles.colors.black500)};
  white-space: nowrap;
  &:hover:not(:disabled) { background: ${({ $danger }) => ($danger ? styles.colors.red200 : '#f7f9fc')}; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

export const PrimaryButton = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  height: 36px; padding: 0 16px; border-radius: 8px; border: none;
  background: ${styles.colors.primary600}; color: ${styles.colors.white}; cursor: pointer;
  font-family: NunitoSans; font-size: 13px; font-weight: 700;
  white-space: nowrap;
  &:hover:not(:disabled) { background: ${styles.colors.primary500}; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

export const TextInput = styled.input`
  height: 36px; min-width: 0; box-sizing: border-box;
  padding: 0 12px; border-radius: 8px; outline: none;
  border: 1.5px solid #dde3ee; background: ${styles.colors.white};
  font-family: NunitoSans; font-size: 13px; color: ${styles.colors.black600};
  &::placeholder { color: ${styles.colors.black300}; }
  &:focus { border-color: ${styles.colors.primary600}; box-shadow: 0 0 0 3px rgba(89,100,255,0.08); }
`;

export const Select = styled.select`
  height: 36px; padding: 0 10px; border-radius: 8px; outline: none;
  border: 1.5px solid #dde3ee; background: ${styles.colors.white}; cursor: pointer;
  font-family: NunitoSans; font-size: 13px; color: ${styles.colors.black600};
`;

const AvatarImg = styled.img<{ $size: number }>`
  width: ${({ $size }) => $size}px; height: ${({ $size }) => $size}px;
  border-radius: 50%; object-fit: cover; flex-shrink: 0;
`;

const AvatarInitials = styled.span<{ $size: number }>`
  width: ${({ $size }) => $size}px; height: ${({ $size }) => $size}px;
  border-radius: 50%; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: ${styles.colors.primary100}; color: ${styles.colors.primary600};
  font-family: NunitoSans; font-size: ${({ $size }) => Math.round($size * 0.36)}px; font-weight: 800;
`;
